import {AuthModel} from './_models'
import {refreshToken} from './_requests'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const AUTH_ROLES_LOCAL_STORAGE_KEY = 'kt-auth-roles-react-v'
// Routes that should remain accessible without authentication
const PUBLIC_ROUTES = ['/program']

// AuthModel güncellenmeli, birden fazla rolü ve token'ı destekleyecek şekilde genişletilmeli
interface AuthRolesModel {
  roles: AuthModel[] // Kullanıcının sahip olduğu tüm roller ve token'lar
  activeRoleIndex: number // Aktif olan rolün index'i
}

const getAuthRoles = (): AuthRolesModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_ROLES_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const authRoles: AuthRolesModel = JSON.parse(lsValue) as AuthRolesModel
    if (authRoles) {
      return authRoles
    }
  } catch (error) {
    console.error('AUTH ROLES LOCAL STORAGE PARSE ERROR', error)
  }
}

const setAuthRoles = (authRoles: AuthRolesModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(authRoles)
    localStorage.setItem(AUTH_ROLES_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH ROLES LOCAL STORAGE SAVE ERROR', error)
  }
}

const setActiveRole = (index: number) => {
  const authRoles = getAuthRoles()
  if (authRoles) {
    authRoles.activeRoleIndex = index
    setAuthRoles(authRoles)
  }
}

const getActiveAuth = (): AuthModel | undefined => {
  const authRoles = getAuthRoles()
  if (authRoles && authRoles.roles.length > 0) {
    return authRoles.roles[authRoles.activeRoleIndex]
  }
}

const removeAuthRoles = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_ROLES_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH ROLES LOCAL STORAGE REMOVE ERROR', error)
  }
}

// Axios setup fonksiyonu güncellenmeli, aktif rol için token'ı kullanmalı
export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'
  axios.interceptors.request.use(
    (config: {headers: {Authorization: string}}) => {
      const auth = getActiveAuth()
      if (auth && auth.token) {
        config.headers.Authorization = `Bearer ${auth.token}`
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )

  axios.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      const {response} = error
      if (response?.status === 403) {
        const rawMessage =
          typeof response.data === 'string'
            ? response.data
            : response.data?.message || response.data?.Message
        if (typeof rawMessage === 'string' && rawMessage.toLowerCase().includes('instantapi')) {
          window.alert(rawMessage)
          return Promise.reject(error)
        }
      }
      if (response?.status === 403) {
        return Promise.reject(error)
      }

      if (response?.status === 401) {
        const currentPath = window.location.pathname
        if (PUBLIC_ROUTES.some((route) => currentPath.startsWith(route))) {
          return Promise.reject(error)
        }
        const auth = getActiveAuth()
        if (auth && auth.refreshToken) {
          try {
            const {data: authResponse} = await refreshToken(auth.refreshToken)
            const authRoles = getAuthRoles()
            const activeRoleIndex =
              authRoles && authRoles.activeRoleIndex < authResponse.length
                ? authRoles.activeRoleIndex
                : 0
            const activeRoleAuth = authResponse[activeRoleIndex]
            if (!activeRoleAuth) {
              throw new Error('Aktif rol tokeni yenilenemedi.')
            }
            setAuthRoles({roles: authResponse, activeRoleIndex})
            error.config.headers.Authorization = `Bearer ${activeRoleAuth.token}`
            return axios(error.config)
          } catch (err) {
            removeAuthRoles()
            window.location.href = '/auth/login'
          }
        } else {
          removeAuthRoles()
          window.location.href = '/auth/login'
        }
      }
      return Promise.reject(error)
    }
  )
}

export {
  getAuthRoles,
  setAuthRoles,
  setActiveRole,
  getActiveAuth,
  removeAuthRoles,
  AUTH_LOCAL_STORAGE_KEY,
}
